import { zodResolver } from '@hookform/resolvers/zod';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { addMinutes, endOfDay, formatISO, startOfDay } from 'date-fns';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { EventAttendeeTypeEnum } from '../Interfaces/Calendar/EventAttendeeTypeEnum';
import { EventBodyContentTypeEnum } from '../Interfaces/Calendar/EventBodyContentTypeEnum';
import { ICalendarEvent } from '../Interfaces/Calendar/ICalendarEvent';
import {
  calendarEventFormSchema,
  ICalendarEventForm,
} from '../Interfaces/Calendar/ICalendarEventForm';
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  DATE_TIME_VIEW,
  DATE_VIEW,
  Messages,
} from '../constants';
import { useAlert } from '../context/AlertContext';
import { showConfirmDialogComponent } from '../core/ConfirmDialog';
import { LoadingDialog } from '../core/LoadingDialog';
import { CreatableSearchableMultiSelectAutocomplete } from '../core/inputs/CreatableSearchableMultiSelectAutocomplete';
import { DateTimePickerInput } from '../core/inputs/DateTimePickerInput';
import { SwitchInput } from '../core/inputs/SwitchInput';
import { TextFieldInput } from '../core/inputs/TextFieldInput';
import {
  createOption,
  issueAttendees,
  issueOptionLabel,
  isValidEmail,
  transformEvent,
} from './utilities';
import {
  useCreateCalendarEvent,
  useDeleteCalendarEvent,
  useUpdateCalendarEvent,
} from '../hooks/calendarHooks';
import { useGetLegalClients } from '../hooks/reactQuerys/LegalClientQueries';

interface EventFormProps {
  userId: number;
  defaultValues: ICalendarEventForm;
  unmount: (editedEvent?: ICalendarEventForm, isDeleted?: boolean) => void;
}

export const EventFormDialog: React.FC<EventFormProps> = ({
  userId,
  defaultValues,
  unmount,
}) => {
  const { showAlert } = useAlert();
  const theme = useTheme();

  const formMethods = useForm<ICalendarEventForm>({
    defaultValues,
    resolver: zodResolver(calendarEventFormSchema),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = formMethods;

  const isEdit = useMemo(() => !!defaultValues.id, [defaultValues]);
  const isDatePickerEndError = useMemo(
    () => !!errors.end,
    [errors.end, errors.start]
  );
  const isDatePickerStartError = useMemo(
    () => !!errors.start,
    [errors.end, errors.start]
  );
  const isAllDay = watch('isAllDay');
  const startDateTime = watch('start.dateTime');
  const endDateTime = watch('end.dateTime');

  // Set minDateTime for end.dateTime to prevent it from being before start.dateTime (when not all-day)
  const endMinDateTime = useMemo(
    () => (startDateTime ? new Date(startDateTime) : undefined),
    [startDateTime, isAllDay]
  );

  // Adjust start and end times when isAllDay changes
  useEffect(() => {
    if (isAllDay) {
      if (startDateTime) {
        const start = new Date(startDateTime);
        setValue(
          'start.dateTime',
          formatISO(startOfDay(start), { representation: 'complete' }),
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
      }
      if (endDateTime) {
        const end = new Date(endDateTime);
        setValue(
          'end.dateTime',
          formatISO(endOfDay(end), { representation: 'complete' }),
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
      }
    }
  }, [isAllDay, startDateTime, endDateTime, setValue]);

  // Reset end.dateTime if start.dateTime is set later than end.dateTime (when not all-day)
  useEffect(() => {
    if (!startDateTime || !endDateTime) return;

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

    if (start > end) {
      const newEndDateTime = addMinutes(start, 30);
      setValue(
        'end.dateTime',
        formatISO(newEndDateTime, { representation: 'complete' }),
        { shouldValidate: true, shouldDirty: true }
      );
    }
  }, [startDateTime, endDateTime, setValue]);

  const { createCalendarEvent, isPendingCreateCalendarEvent } =
    useCreateCalendarEvent(userId);
  const { updateCalendarEvent, isPendingUpdateCalendarEvent } =
    useUpdateCalendarEvent(userId);
  const { deleteCalendarEvent, isPendingDeleteCalendarEvent } =
    useDeleteCalendarEvent(userId);
  const { legalClients, isLoadingLegalClients } = useGetLegalClients();

  const allAttendees = useMemo(() => {
    if (!legalClients || isLoadingLegalClients) return [];
    return issueAttendees(legalClients);
  }, [legalClients, isLoadingLegalClients]);

  const onSubmit = useCallback(
    async (data: ICalendarEventForm) => {
      try {
        const requiredAttendees = data.requiredAttendees.map(a => ({
          emailAddress: {
            name: a.emailAddress.name,
            address: a.emailAddress.address,
          },
          type: EventAttendeeTypeEnum.Required,
        }));

        const optionalAttendees = data.optionalAttendees.map(a => ({
          emailAddress: {
            name: a.emailAddress.name,
            address: a.emailAddress.address,
          },
          type: EventAttendeeTypeEnum.Optional,
        }));

        const submitData: ICalendarEvent = {
          ...data,
          body: { ...data.body, contentType: EventBodyContentTypeEnum.Text },
          attendees: [...requiredAttendees, ...optionalAttendees],
        };

        const updatedOrCreatedEvent: ICalendarEvent = submitData.id
          ? await updateCalendarEvent(submitData)
          : await createCalendarEvent(submitData);

        showAlert({
          text: Messages.ActionSuccessfully,
        });

        unmount(transformEvent(updatedOrCreatedEvent, legalClients!));
      } catch (error) {
        console.error(error);
        showAlert({
          text: Messages.GeneralError,
          severity: 'error',
        });
      }
    },
    [createCalendarEvent, showAlert, unmount, updateCalendarEvent]
  );

  const onDelete = useCallback(async () => {
    const isConfirm = await showConfirmDialogComponent(
      'Warnung',
      'Sind Sie sicher, dass Sie dieses Ereignis löschen wollen?',
      'Löschen',
      'Schließen',
      theme
    );
    if (isConfirm) {
      deleteCalendarEvent(defaultValues);
      unmount(defaultValues, true);
    }
  }, [defaultValues.id, deleteCalendarEvent, theme]);

  if (
    isPendingCreateCalendarEvent ||
    isPendingUpdateCalendarEvent ||
    isPendingDeleteCalendarEvent ||
    isLoadingLegalClients
  )
    return <LoadingDialog maxWidth='sm' />;

  return (
    <Dialog open={true} onClose={() => unmount()} maxWidth='sm' fullWidth>
      <DialogTitle
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 3,
          py: 2,
        }}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          width='100%'
        >
          <Typography variant='h5'>
            {isEdit ? 'Ereignis bearbeiten' : 'Neues Ereignis erstellen'}
          </Typography>
          {isEdit && (
            <IconButton onClick={onDelete}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <FormProvider {...formMethods}>
        <Box component='form' noValidate onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <TextFieldInput
              name='subject'
              label='Titel'
              required
              size='small'
              margin='dense'
              fullWidth
            />
            <CreatableSearchableMultiSelectAutocomplete
              name='requiredAttendees'
              label='Erforderliche Teilnehmer'
              hint='Geben Sie eine valide E-Mail-Adresse ein, um einen externen Teilnehmer hinzuzufügen'
              options={allAttendees}
              getOptionLabel={issueOptionLabel}
              isOptionDisabled={option => !option.emailAddress.address}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              createOption={input => createOption(input)}
              validateInput={input => isValidEmail(input)}
              textFieldProps={{
                size: 'small',
                margin: 'dense',
                fullWidth: true,
              }}
            />
            <CreatableSearchableMultiSelectAutocomplete
              name='optionalAttendees'
              label='Optionale Teilnehmer'
              hint='Geben Sie eine valide E-Mail-Adresse ein, um einen externen Teilnehmer hinzuzufügen'
              options={allAttendees}
              getOptionLabel={issueOptionLabel}
              isOptionDisabled={option => !option.emailAddress.address}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              createOption={input => createOption(input)}
              validateInput={input => isValidEmail(input)}
              textFieldProps={{
                size: 'small',
                margin: 'dense',
                fullWidth: true,
              }}
            />
            <Box display='flex' alignItems='center' gap={2}>
              <Box flex={1} sx={{ mb: isDatePickerEndError ? 3 : 0 }}>
                <DateTimePickerInput
                  name='start.dateTime'
                  label='Startzeit'
                  views={isAllDay ? DATE_VIEW : DATE_TIME_VIEW}
                  format={isAllDay ? DATE_FORMAT : DATE_TIME_FORMAT}
                  textFieldProps={{
                    required: true,
                    size: 'small',
                    margin: 'dense',
                    fullWidth: true,
                  }}
                />
              </Box>
              <Box flex={1} sx={{ mb: isDatePickerStartError ? 3 : 0 }}>
                <DateTimePickerInput
                  name='end.dateTime'
                  label='Endzeit'
                  views={isAllDay ? DATE_VIEW : DATE_TIME_VIEW}
                  format={isAllDay ? DATE_FORMAT : DATE_TIME_FORMAT}
                  textFieldProps={{
                    required: true,
                    size: 'small',
                    margin: 'dense',
                    fullWidth: true,
                  }}
                  minDateTime={endMinDateTime} // Prevent end before start
                />
              </Box>
              <Box
                sx={{
                  mb: isDatePickerStartError || isDatePickerEndError ? 3 : 0,
                }}
              >
                <SwitchInput name='isAllDay' label='Ganztägig' size='small' />
              </Box>
            </Box>
            <TextFieldInput
              name='location.displayName'
              label='Ort'
              size='small'
              margin='dense'
              fullWidth
            />
            <TextFieldInput
              name='body.content'
              label='Beschreibung'
              size='small'
              margin='dense'
              fullWidth
              multiline
              rows={4}
            />
          </DialogContent>
          <DialogActions
            sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
          >
            <Button variant='outlined' onClick={() => unmount()}>
              Abbrechen
            </Button>
            <Button
              type='submit'
              variant='contained'
              disabled={isSubmitting || !isDirty || !isValid}
            >
              {isEdit ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogActions>
        </Box>
      </FormProvider>
    </Dialog>
  );
};
